import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    section: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#f8f9fa',
    },
    sectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        color: '#495057',
        textAlign: 'center',
    },
    table: {
        width: "100%",
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#e9ecef',
        paddingVertical: 3,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
    },
    tableRow: {
        flexDirection: 'row',
        paddingVertical: 3,
        paddingHorizontal: 4,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#f1f3f5',
    },
    tableHeaderCell: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        textAlign: 'center',
    },
    headerCellLeft: {
        textAlign: 'left',
        paddingLeft: 1,
    },
    headerCellRight: {
        textAlign: 'right',
        paddingRight: 1,
    },
    tableCell: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#212529',
        textAlign: 'left',
        paddingHorizontal: 1,
    },
    qtyCell: {
        flex: 1,
        textAlign: 'center',
    },
    typeCell: {
        flex: 1.5,
    },
    partCell: {
        flex: 2,
    },
    descriptionCell: {
        flex: 3,
    },
    costCell: {
        flex: 1.2,
        textAlign: 'right',
    },
    totalRow: {
        flexDirection: 'row',
        backgroundColor: '#e9ecef',
        paddingVertical: 3,
        paddingHorizontal: 4,
        marginTop: 3,
        borderTopWidth: 2,
        borderTopStyle: 'solid',
        borderTopColor: '#495057',
    },
    totalLabel: {
        flex: 6.5,
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        textAlign: 'right',
        paddingRight: 8,
    },
    totalValue: {
        flex: 1.2,
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        textAlign: 'right',
    },
    noDataText: {
        fontSize: 9,
        fontStyle: 'italic',
        color: '#6c757d',
        textAlign: 'center',
        marginTop: 6,
    },
    miscInfo: {
        fontSize: 7,
        fontStyle: 'italic',
        color: '#6c757d',
        marginTop: 1,
    },
});

interface ServicePartsSectionProps {
    parts: Array<{
        qty: number | string;
        type: string;
        part: string;
        description: string;
        cost: number | string;
        isMisc?: boolean;
        location?: string;
        name?: string;
        invoice?: string;
        po?: string;
    }>;
}

const ServicePartsSection: React.FC<ServicePartsSectionProps> = ({
    parts,
}) => {
    const formatQuantity = (qty: number | string) => {
        if (!qty || qty === '') return '';
        const numQty = typeof qty === 'string' ? parseFloat(qty) : qty;
        return isNaN(numQty) ? '' : numQty.toString();
    };

    // Filter out empty parts
    const validParts = parts.filter(part =>
        part.part && part.part.trim() !== '' &&
        part.qty && part.qty !== '' &&
        parseFloat(part.qty.toString()) > 0
    );

    if (validParts.length === 0) {
        return (
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>Parts Used</Text>
                <Text style={styles.noDataText}>No parts used</Text>
            </View>
        );
    }

    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>Parts Used</Text>
            <View style={styles.table}>
                <View style={styles.tableHeader}>
                    <Text style={[styles.tableHeaderCell, styles.qtyCell]}>Qty</Text>
                    <Text style={[styles.tableHeaderCell, styles.typeCell, styles.headerCellLeft]}>Type</Text>
                    <Text style={[styles.tableHeaderCell, styles.partCell, styles.headerCellLeft]}>Part #</Text>
                    <Text style={[styles.tableHeaderCell, styles.descriptionCell, styles.headerCellLeft]}>Description</Text>
                </View>
                {validParts.map((part, index) => (
                    <View key={index}>
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, styles.qtyCell]}>
                                {formatQuantity(part.qty)}
                            </Text>
                            <Text style={[styles.tableCell, styles.typeCell]}>
                                {part.type || ''}
                            </Text>
                            <Text style={[styles.tableCell, styles.partCell]}>
                                {part.part || ''}
                            </Text>
                            <Text style={[styles.tableCell, styles.descriptionCell]}>
                                {part.description || ''}
                            </Text>
                        </View>
                        {part.isMisc && (part.location || part.name || part.invoice || part.po) && (
                            <View style={[styles.tableRow, { backgroundColor: '#f8f9fa' }]}>
                                <Text style={[styles.miscInfo, { flex: 7.5, paddingLeft: 10 }]}>
                                    {part.location && `Location: ${part.location} • `}
                                    {part.name && `Name: ${part.name} • `}
                                    {part.invoice && `Invoice: ${part.invoice} • `}
                                    {part.po && `PO: ${part.po}`}
                                </Text>
                            </View>
                        )}
                    </View>
                ))}
            </View>
        </View>
    );
};

export default ServicePartsSection;
